// src/components/RevenueRoom.js
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  FaHotel, FaArrowLeft, FaTimes, FaUser, FaMoneyBillWave,
  FaBuilding, FaCheckCircle, FaExclamationTriangle, FaSearch,
  FaArrowRight, FaBed, FaKey, FaUsers, FaKeyboard, FaTh,
  FaChevronDown, FaChevronUp, FaPlay, FaClock, FaUtensils
} from 'react-icons/fa';
import { fetchRoomsData, fetchHotels, validateRoom, getAllRoomsByFloor } from "../utils/roomValidation";
import enviar_pedidokds from "../utils/enviar_pedidokds";
import { IP } from "../configuracion.js";
import "../styles/RevenueRoom.css";

const RevenueRoom = ({
  revenue,
  property,
  onConfirm,
  onCancel,
  isHotelEnabled,
  occupiedRooms = [],
  onSelectOccupiedRoom
}) => {
  const [roomNumber, setRoomNumber] = useState('');
  const [clientCount, setClientCount] = useState('');
  const [roomError, setRoomError] = useState(null);
  const [step, setStep] = useState('list');
  const [roomsData, setRoomsData] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [validatedRoom, setValidatedRoom] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [showFloorPlan, setShowFloorPlan] = useState(true);
  const [expandedFloors, setExpandedFloors] = useState({});

  // Panel de revisión pre-cocina: habitaciones expandidas + overrides de estado de ítem
  const [expandedRooms, setExpandedRooms] = useState({});
  const [itemStateOverrides, setItemStateOverrides] = useState({}); // { [id_producto]: "pendiente" }
  const [updatingItems, setUpdatingItems] = useState(new Set());

  // Cargar hoteles
  useEffect(() => {
    const loadHotels = async () => {
      const hotelsList = await fetchHotels(property, IP);
      setHotels(hotelsList);
      if (hotelsList.length > 0) {
        setSelectedHotel(hotelsList[0]);
      }
    };
    loadHotels();
  }, [property]);

  // Cargar habitaciones cuando cambia el hotel
  useEffect(() => {
    const loadRooms = async () => {
      if (!selectedHotel) return;
      setLoadingRooms(true);
      const rooms = await fetchRoomsData(property, IP, selectedHotel.id);
      setRoomsData(rooms);
      setLoadingRooms(false);
    };
    loadRooms();
  }, [property, selectedHotel]);

  useEffect(() => {
    if (occupiedRooms.length === 0 && step === 'list' && !loadingRooms) {
      setStep('floorPlan');
    }
  }, [occupiedRooms, step, loadingRooms]);

  const validateAndContinue = async () => {
    if (!roomNumber || roomNumber.trim() === '') {
      setRoomError('Por favor ingresa un número de habitación');
      return;
    }

    const validation = validateRoom(roomNumber, roomsData);
    
    if (!validation.valid) {
      setRoomError(validation.error);
      return;
    }

    setValidatedRoom(validation.roomInfo);
    setRoomError(null);
    setStep('clientCount');
  };

  const confirmClientCount = () => {
    const parsedClientCount = parseInt(clientCount, 10);
    if (!parsedClientCount || isNaN(parsedClientCount) || parsedClientCount <= 0) {
      setRoomError('Ingresa un número válido de clientes');
      return;
    }
    if (parsedClientCount > 50) {
      setRoomError('El número máximo de clientes es 50');
      return;
    }
    onConfirm({
      roomNumber: roomNumber.trim(),
      roomInfo: validatedRoom,
      clientCount: parsedClientCount
    });
  };

  const handleKeyPress = (key, isForRoom = true) => {
    const setter = isForRoom ? setRoomNumber : setClientCount;
    const current = isForRoom ? roomNumber : clientCount;
    const maxLength = isForRoom ? 10 : 2;

    if (key === "⌫") {
      setter(prev => prev.slice(0, -1));
    } else if (key === "Limpiar") {
      setter("");
      if (isForRoom) setRoomError(null);
    } else if (typeof key === "number") {
      if (current.length < maxLength) {
        setter(prev => prev + key);
        if (isForRoom) setRoomError(null);
      }
    }
  };

  const selectRoom = (room) => {
    if (room.activa !== 'Y') {
      setRoomError(`La habitación ${room.room} no está disponible`);
      return;
    }
    setRoomNumber(String(room.room));
    setValidatedRoom(room);
    setRoomError(null);
    setStep('clientCount');
  };

  const selectOccupiedRoom = (room) => {
    if (onSelectOccupiedRoom) {
      onSelectOccupiedRoom(room);
    }
  };

  const toggleRoomExpanded = (roomId) => {
    setExpandedRooms(prev => ({ ...prev, [roomId]: !prev[roomId] }));
  };

  const handleSendToPending = useCallback(async (idProducto) => {
    if (updatingItems.has(idProducto)) return;
    // Optimistic update
    setUpdatingItems(prev => new Set(prev).add(idProducto));
    setItemStateOverrides(prev => ({ ...prev, [idProducto]: "pendiente" }));
    try {
      await enviar_pedidokds({ id: idProducto, estado: "pendiente" }, null, property);
    } catch (err) {
      console.error("[RevenueRoom] Error cambiando estado a pendiente:", err);
      // Revert optimistic update on error
      setItemStateOverrides(prev => {
        const next = { ...prev };
        delete next[idProducto];
        return next;
      });
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(idProducto);
        return next;
      });
    }
  }, [updatingItems, property]);

  const toggleFloor = (floor) => {
    setExpandedFloors(prev => ({
      ...prev,
      [floor]: !prev[floor]
    }));
  };

  const { floors, sortedFloors } = getAllRoomsByFloor(roomsData);
  const occupiedRoomNumbers = occupiedRooms.map(r => String(r.roomNumber));

  const renderHotelSelector = () => {
    if (hotels.length <= 1) return null;

    return (
      <div className="rev-hotel-selector">
        <label className="rev-hotel-label">
          <FaHotel /> Seleccionar Hotel:
        </label>
        <select 
          className="rev-hotel-select"
          value={selectedHotel?.id || ''}
          onChange={(e) => {
            const hotel = hotels.find(h => h.id === e.target.value);
            setSelectedHotel(hotel);
            setSelectedFloor(null);
          }}
        >
          {hotels.map(hotel => (
            <option key={hotel.id} value={hotel.id}>
              {hotel.nombre}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderFloorPlan = () => {
    if (loadingRooms) {
      return (
        <div className="rev-room-empty-list">
          <div className="rev-loader"></div>
          <p>Cargando floor plan...</p>
        </div>
      );
    }

    if (roomsData.length === 0) {
      return (
        <div className="rev-room-empty-list">
          <FaExclamationTriangle size={48} className="rev-empty-icon" />
          <p>No hay habitaciones configuradas para este hotel</p>
        </div>
      );
    }

    const activeCount = roomsData.filter(r => r.activa === 'Y').length;
    const inactiveCount = roomsData.filter(r => r.activa !== 'Y').length;

    return (
      <>
        <div className="rev-stats-badge">
          <span className="rev-total-rooms">🏨 Total: {roomsData.length} habitaciones</span>
          <span className="rev-active-rooms">✅ Activas: {activeCount}</span>
          {inactiveCount > 0 && (
            <span className="rev-inactive-rooms">⛔ Inactivas: {inactiveCount}</span>
          )}
        </div>

        <div className="rev-floorplan-container">
          {sortedFloors.map(floor => {
            const floorRooms = floors[floor];
            const isExpanded = expandedFloors[floor] !== false;
            
            return (
              <div key={floor} className="rev-floor-section">
                <div className="rev-floor-header" onClick={() => toggleFloor(floor)}>
                  <div className="rev-floor-title">
                    <FaBuilding />
                    <span>Piso {floor}</span>
                    <span className="rev-floor-count">
                      ({floorRooms.length} habs)
                    </span>
                  </div>
                  <button className="rev-floor-toggle">
                    {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
                
                {isExpanded && (
                  <div className="rev-floor-rooms">
                    {floorRooms.map(room => {
                      const isOccupied = occupiedRoomNumbers.includes(String(room.room));
                      const isActive = room.activa === 'Y';
                      
                      return (
                        <div
                          key={room.room}
                          className={`rev-floor-room ${!isActive ? 'rev-room-inactive' : ''} ${isOccupied ? 'rev-room-occupied' : ''}`}
                          onClick={() => isActive && !isOccupied && selectRoom(room)}
                        >
                          <div className="rev-room-status-icon">
                            {!isActive && <FaExclamationTriangle className="rev-status-inactive" />}
                            {isActive && isOccupied && <FaKey className="rev-status-occupied" />}
                            {isActive && !isOccupied && <FaCheckCircle className="rev-status-available" />}
                          </div>
                          <div className="rev-room-number-display">{room.room}</div>
                          <div className="rev-room-status-label">
                            {!isActive && "Inactiva"}
                            {isActive && isOccupied && "Ocupada"}
                            {isActive && !isOccupied && "Disponible"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const renderManualInput = () => {
    const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, "⌫", 0];
    
    return (
      <div className="rev-manual-input-container">
        <div className="rev-room-display">
          <input 
            type="text" 
            value={roomNumber} 
            readOnly 
            placeholder="Número de habitación" 
            className={`rev-room-input ${roomError ? 'rev-input-error' : ''}`}
          />
        </div>
        {roomError && <div className="rev-room-error">{roomError}</div>}
        
        <div className="rev-numeric-keypad">
          {numbers.map((key) => (
            <button 
              key={key} 
              onClick={() => handleKeyPress(key, true)} 
              className={`rev-key-button ${typeof key === "number" ? "rev-key-number" : "rev-key-delete"}`}
            >
              {key}
            </button>
          ))}
          <button onClick={validateAndContinue} className="rev-key-button rev-key-confirm" disabled={!roomNumber}>
            Validar y continuar
          </button>
        </div>
      </div>
    );
  };

  const renderClientKeypad = () => {
    const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, "⌫", 0];
    return (
      <div className="rev-room-keypad">
        <div className="rev-room-display">
          <input 
            type="text" 
            value={clientCount} 
            readOnly 
            placeholder="Número de clientes" 
            className="rev-room-input" 
          />
        </div>
        {roomError && <div className="rev-room-error">{roomError}</div>}
        <div className="rev-numeric-keypad">
          {numbers.map((key) => (
            <button 
              key={key} 
              onClick={() => handleKeyPress(key, false)} 
              className={`rev-key-button ${typeof key === "number" ? "rev-key-number" : "rev-key-delete"}`}
            >
              {key}
            </button>
          ))}
          <button onClick={confirmClientCount} className="rev-key-button rev-key-confirm" disabled={!clientCount || parseInt(clientCount) === 0}>
            Confirmar
          </button>
        </div>
      </div>
    );
  };

  const renderOccupiedRoomsList = () => {
    if (!occupiedRooms || occupiedRooms.length === 0) {
      return null;
    }

    return (
      <div className="rev-occupied-section">
        <div className="rev-section-header">
          <h3>📋 Cuentas activas</h3>
        </div>
        <div className="rev-occupied-scroll">
          {occupiedRooms.map(room => {
            const isExpanded = !!expandedRooms[room.id];

            // Normalizar estado para comparar sin importar mayúsculas/variantes
            const normEstado = (s) => (s || "").toLowerCase().trim();
            const isEspera  = (s) => normEstado(s) === "espera" || normEstado(s) === "en espera";
            const isPending = (s) => normEstado(s) === "pendiente";

            // Extraer todos los ítems del cheque con estado pendiente o espera
            const allItems = (room.chequeData?.pedidos || []).flatMap(pedido =>
              (pedido.productos || []).map(p => ({
                id_producto: p.id_producto,
                nombre: p.nombre,
                cantidad: p.cantidad,
                estado: itemStateOverrides[p.id_producto] ?? (p.estado || "pendiente"),
              }))
            ).filter(p => isPending(p.estado) || isEspera(p.estado));

            const pendingItems = allItems.filter(p => isPending(p.estado));
            const waitingItems = allItems.filter(p => isEspera(p.estado));

            return (
              <div key={room.id} className="rev-occupied-card-wrapper">
                {/* Cabecera de la cuenta — clic abre/cierra el desglose */}
                <div
                  className="rev-occupied-card"
                  onClick={() => toggleRoomExpanded(room.id)}
                >
                  <div className="rev-occupied-icon">
                    <FaHotel />
                  </div>
                  <div className="rev-occupied-info">
                    <div className="rev-occupied-room">Habitación {room.roomNumber}</div>
                    <div className="rev-occupied-guest">
                      <FaUser /> {room.guestName}
                    </div>
                    <div className="rev-occupied-details">
                      <span><FaUsers /> {room.clientCount} personas</span>
                      <span><FaMoneyBillWave /> ${parseFloat(room.total_cheque || 0).toFixed(2)}</span>
                      {waitingItems.length > 0 && (
                        <span className="rev-waiting-badge-inline">
                          <FaClock /> {waitingItems.length} en espera
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="rev-occupied-card-actions">
                    <button
                      className="rev-open-cheque-btn"
                      title="Abrir cheque en POS"
                      onClick={e => { e.stopPropagation(); selectOccupiedRoom(room); }}
                    >
                      <FaArrowRight />
                    </button>
                    <div className="rev-expand-toggle">
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                  </div>
                </div>

                {/* Desglose de ítems — solo visible cuando expandido */}
                {isExpanded && allItems.length > 0 && (
                  <div className="rev-items-panel">
                    {waitingItems.length > 0 && (
                      <div className="rev-items-group">
                        <div className="rev-items-group-label rev-label-waiting">
                          <FaClock /> En espera ({waitingItems.length})
                        </div>
                        {waitingItems.map(item => (
                          <div key={item.id_producto} className="rev-item-row rev-item-waiting">
                            <span className="rev-item-qty">{item.cantidad}x</span>
                            <span className="rev-item-name">{item.nombre}</span>
                            <button
                              className="rev-send-to-pending-btn"
                              disabled={updatingItems.has(item.id_producto)}
                              onClick={() => handleSendToPending(item.id_producto)}
                              title="Enviar a cocina (cambiar a pendiente)"
                            >
                              {updatingItems.has(item.id_producto)
                                ? <span className="rev-btn-spinner" />
                                : <><FaPlay /> Enviar</>
                              }
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {pendingItems.length > 0 && (
                      <div className="rev-items-group">
                        <div className="rev-items-group-label rev-label-pending">
                          <FaUtensils /> En cocina ({pendingItems.length})
                        </div>
                        {pendingItems.map(item => (
                          <div key={item.id_producto} className="rev-item-row rev-item-pending">
                            <span className="rev-item-qty">{item.cantidad}x</span>
                            <span className="rev-item-name">{item.nombre}</span>
                            <span className="rev-item-status-chip">✓ Pendiente</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {allItems.length === 0 && (
                      <div className="rev-items-empty">Sin ítems pendientes</div>
                    )}
                  </div>
                )}

                {isExpanded && allItems.length === 0 && (
                  <div className="rev-items-panel rev-items-empty">Sin ítems pendientes o en espera</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderHeader = () => (
    <div className="rev-room-header">
      <FaHotel size={28} className="rev-room-icon" />
      <div className="rev-header-info">
        <h2>{revenue?.name || 'Room Services'}</h2>
        {selectedHotel && <span className="rev-hotel-name">{selectedHotel.nombre}</span>}
      </div>
      <button className="rev-room-close" onClick={onCancel} aria-label="Cerrar">
        <FaTimes />
      </button>
    </div>
  );

// ============================================
// REEMPLAZA COMPLETA ESTA FUNCIÓN
// ============================================

const renderListStep = () => (
  <div className="rev-room-step">

    {/* HEADER */}
    {renderHeader()}

    {/* CONTENIDO PRINCIPAL */}
    <div className="rev-room-content">

      {/* ÁREA CON SCROLL */}
      <div className="rev-room-scroll-area">

        {/* CUENTAS ACTIVAS */}
        {renderOccupiedRoomsList()}

        {/* TOGGLE VISTA */}
        <div className="rev-view-toggle">

          <button
            className={`rev-toggle-btn ${showFloorPlan ? 'active' : ''}`}
            onClick={() => setShowFloorPlan(true)}
          >
            <FaTh />
            <span>Floor Plan</span>
          </button>

          <button
            className={`rev-toggle-btn ${!showFloorPlan ? 'active' : ''}`}
            onClick={() => setShowFloorPlan(false)}
          >
            <FaKeyboard />
            <span>Ingreso manual</span>
          </button>

        </div>

        {/* FLOOR PLAN O INPUT */}
        {showFloorPlan ? (
          <>
            {renderHotelSelector()}

            <div className="rev-room-floorplan-wrapper">
              {renderFloorPlan()}
            </div>
          </>
        ) : (
          <div className="rev-room-manual-wrapper">
            {renderManualInput()}
          </div>
        )}

      </div>

    </div>

    {/* FOOTER */}
    <div className="rev-room-footer">

      <button
        className="rev-room-cancel-footer"
        onClick={onCancel}
      >
        Cancelar
      </button>

    </div>

  </div>
);

  const renderRoomStep = () => (
    <div className="rev-room-step">
      {renderHeader()}
      <div className="rev-room-content">
        <p className="rev-room-instruction">Ingresa el número de habitación</p>
        {renderManualInput()}
        <div className="rev-room-buttons">
          <button className="rev-room-back" onClick={() => setStep('list')}>
            <FaArrowLeft /> Volver a habitaciones
          </button>
        </div>
      </div>
    </div>
  );

  const renderClientCountStep = () => (
    <div className="rev-room-step">
      {renderHeader()}
      <div className="rev-room-content">
        <div className="rev-room-info">
          <div className="rev-room-info-item">
            <span className="rev-info-label">Habitación:</span>
            <span className="rev-info-value">{roomNumber}</span>
          </div>
          {validatedRoom && (
            <>
              <div className="rev-room-info-item">
                <span className="rev-info-label">Piso:</span>
                <span className="rev-info-value">{validatedRoom.floor}</span>
              </div>
              <div className="rev-room-info-item rev-info-success">
                <FaCheckCircle className="rev-success-icon" />
                <span className="rev-info-label">Estado:</span>
                <span className="rev-info-value rev-success-text">✓ Habitación disponible</span>
              </div>
            </>
          )}
        </div>
        <p className="rev-room-instruction">¿Cuántas personas son?</p>
        {renderClientKeypad()}
        <div className="rev-room-buttons">
          <button className="rev-room-back" onClick={() => { setStep('list'); setRoomError(null); setClientCount(''); setRoomNumber(''); }}>
            <FaArrowLeft /> Volver
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 'list':
        return renderListStep();
      case 'floorPlan':
        return renderListStep();
      case 'room':
        return renderRoomStep();
      case 'clientCount':
        return renderClientCountStep();
      default:
        return renderListStep();
    }
  };

  return (
    <div className="rev-room-modal-overlay" onClick={onCancel}>
      <div className="rev-room-modal" onClick={e => e.stopPropagation()}>
        {renderStep()}
      </div>
    </div>
  );
};

RevenueRoom.propTypes = {
  revenue: PropTypes.object.isRequired,
  property: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isHotelEnabled: PropTypes.bool.isRequired,
  occupiedRooms: PropTypes.array,
  onSelectOccupiedRoom: PropTypes.func,
};

RevenueRoom.defaultProps = {
  occupiedRooms: [],
  onSelectOccupiedRoom: null,
  isHotelEnabled: false,
};

export default RevenueRoom;